/**
 * Publisher Panel — /publisher
 *
 * Inbox-only view. Every prepared publication is a card with a row of
 * per-destination chips:
 *   - Connector chip   → "Push" button; auto-checkmark after a successful dispatch
 *   - Reserved chip    → manual-style chip labeled "Adapter coming"
 *   - Manual chip      → "Copy" button + user-toggleable Sent checkbox
 *
 * Whether a connector chip is "live" (shows Push) vs "Adapter coming" is
 * derived from `PUBLISHING_REGISTRY` — a destination is live here when its
 * registry entry has `status === "live"` AND `surfaces.includes("long_form")`.
 *
 *
 * Per-chip state lives in `content_publication_destinations`. Sent column,
 * Archive column, and the right-rail tabs have been removed — the chip
 * checkmarks ARE the completion signal. Published items continue to live
 * in Composer > Vault > Archive.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Inbox as InboxIcon,
  Send,
  Settings as SettingsIcon,
} from "lucide-react";
import { toast } from "sonner";

import { AppSeo } from "@/components/AppSeo";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  contentDestinationFormatLabels,
  fetchComposerSnapshot,
  runPublicationDispatch,
  type ContentBrief,
  type ContentBriefPublication,
  type ContentPublishingConnector,
  type ContentDestinationFormat,
} from "@/lib/composerCore";
import {
  copyPublicationText,
  downloadPublicationFile,
} from "@/components/composer/composerPageHelpers";
import {
  PUBLISH_TYPE_BY_KEY,
  isPublishTypeKey,
  CONNECTOR_DEFAULT_PUBLISH_TYPES,
  ALL_PUBLISH_TYPE_KEYS,
  type PublishTypeKey,
} from "@/lib/publishTypes";
import {
  type ManualDestination,
  ensureSeededManualDestinations,
} from "@/lib/workspaceManualDestinations";
import {
  type PublicationDestination,
  listPublicationDestinations,
  recordConnectorDispatch,
  setManualChipMarked,
} from "@/lib/publicationDestinations";
import { ManualDestinationsEditor } from "@/components/publisher/ManualDestinationsEditor";
import {
  isConnectorLive,
  isConnectorUnofficial,
} from "@/lib/publisherConnectorLive";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  clearManualPendingMedia,
  listPendingMediaCounts,
  seedManualPendingMedia,
  type PendingMediaCount,
} from "@/lib/pendingMedia";
import { supabase } from "@/integrations/supabase/client";


type Snapshot = Awaited<ReturnType<typeof fetchComposerSnapshot>>;


const PendingBadge = ({ count }: { count: number }) =>
  count > 0 ? (
    <Badge
      variant="destructive"
      className="h-4 min-w-[1rem] justify-center px-1 text-[10px]"
      title={`${count} image${count === 1 ? "" : "s"} still to re-upload`}
    >
      {count}
    </Badge>
  ) : null;

const ConnectorChip = ({
  connector,
  done,
  busy,
  pending,
  onPush,
}: {
  connector: ContentPublishingConnector;
  done: boolean;
  busy: boolean;
  pending: number;
  onPush: () => void;
}) => {
  const live = isConnectorLive(connector);
  const unofficial = isConnectorUnofficial(connector);
  return (
    <div
      className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
        done ? "border-primary/40 bg-primary/5" : "border-border bg-background"
      }`}
    >
      <span className="font-medium">{connector.display_name || connector.connector_type}</span>
      {unofficial ? (
        <Badge
          variant="outline"
          className="h-4 px-1.5 text-[10px] uppercase tracking-wide"
          title="Unofficial — uses a reverse-engineered endpoint; may break without notice"
        >
          Unofficial
        </Badge>
      ) : null}
      <PendingBadge count={pending} />
      {done ? (
        <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">Sent ✓</Badge>
      ) : live ? (
        <Button
          size="sm"
          variant="ghost"
          className="h-5 gap-1 px-1.5 text-[10px]"
          disabled={busy}
          onClick={onPush}
        >
          <Send className="h-3 w-3" /> Push
        </Button>
      ) : (
        <span className="text-[10px] text-muted-foreground">Adapter coming</span>
      )}
    </div>
  );
};

const ManualChip = ({
  destination,
  marked,
  busy,
  body,
  pending,
  onToggle,
}: {
  destination: ManualDestination;
  marked: boolean;
  busy: boolean;
  body: string;
  pending: number;
  onToggle: (next: boolean) => void;
}) => (
  <div
    className={`flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs ${
      marked ? "border-primary/40 bg-primary/5" : "border-border bg-background"
    }`}
  >
    <span className="font-medium">{destination.label}</span>
    <PendingBadge count={pending} />
    <Button
      size="sm"
      variant="ghost"
      className="h-5 w-5 p-0"
      title="Copy body"
      onClick={() => void copyPublicationText(body)}
    >
      <Copy className="h-3 w-3" />
    </Button>
    <Checkbox
      checked={marked}
      disabled={busy}
      onCheckedChange={(v) => onToggle(v === true)}
      aria-label={`Mark ${destination.label} as sent`}
    />
  </div>
);

/* ------------------------------ Publication card --------------------------- */

const PublicationCard = ({
  pub,
  brief,
  connectors,
  manualDests,
  destRows,
  pendingByDest,
  userId,
  onChanged,
}: {
  pub: ContentBriefPublication;
  brief: ContentBrief | undefined;
  connectors: ContentPublishingConnector[];
  manualDests: ManualDestination[];
  destRows: PublicationDestination[];
  pendingByDest: Map<string, PendingMediaCount>;
  userId: string;
  onChanged: () => void | Promise<void>;
}) => {
  const briefTitle = brief?.title || "Untitled piece";
  const [expanded, setExpanded] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const publishTypeKey: PublishTypeKey | null = isPublishTypeKey(pub.publish_type_key)
    ? pub.publish_type_key
    : null;
  const publishTypeLabel = publishTypeKey
    ? PUBLISH_TYPE_BY_KEY[publishTypeKey].label
    : contentDestinationFormatLabels[pub.destination_format];

  // Filter chips by publish type. Manual destinations carry their own
  // applies_to list; connector chips fall back to a per-connector-type
  // default map (webhook = all types). If we don't know the publish type
  // we show everything to avoid hiding chips by accident.
  const filteredConnectors = publishTypeKey
    ? connectors.filter((c) => {
        const allowed = CONNECTOR_DEFAULT_PUBLISH_TYPES[c.connector_type] ?? ALL_PUBLISH_TYPE_KEYS;
        return allowed.includes(publishTypeKey);
      })
    : connectors;
  const filteredManualDests = publishTypeKey
    ? manualDests.filter((d) => d.applies_to_publish_types.includes(publishTypeKey))
    : manualDests;

  const connectorDoneIds = new Set(
    destRows.filter((r) => r.kind === "connector" && r.connector_id).map((r) => r.connector_id!),
  );
  const manualMarkedIds = new Set(
    destRows.filter((r) => r.kind === "manual" && r.manual_destination_id).map((r) => r.manual_destination_id!),
  );

  const handlePushConnector = async (connector: ContentPublishingConnector) => {
    const key = `c:${connector.id}`;
    setBusyKey(key);
    try {
      const dispatch = await runPublicationDispatch(pub.workspace_id, {
        publicationId: pub.id,
        connectorId: connector.id,
        allowStale: false,
      });
      await recordConnectorDispatch({
        workspaceId: pub.workspace_id,
        publicationId: pub.id,
        connectorId: connector.id,
        dispatchId: dispatch.id,
        createdByUserId: userId,
      });
      toast.success(`Pushed to ${connector.display_name || connector.connector_type}`);
      await onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Push failed");
    } finally {
      setBusyKey(null);
    }
  };

  const handleToggleManual = async (dest: ManualDestination, next: boolean) => {
    const key = `m:${dest.id}`;
    setBusyKey(key);
    try {
      await setManualChipMarked({
        workspaceId: pub.workspace_id,
        publicationId: pub.id,
        manualDestinationId: dest.id,
        marked: next,
        createdByUserId: userId,
      });
      // Stage 7: mirror the chip toggle into the pending-media checklist so
      // manual destinations get the same image-followup workflow as Kit/Bento.
      try {
        if (next) {
          await seedManualPendingMedia({
            scope: "publication",
            workspaceId: pub.workspace_id,
            parentId: pub.id,
            destinationId: dest.id,
            body: pub.prepared_body ?? "",
          });
        } else {
          await clearManualPendingMedia({
            scope: "publication",
            workspaceId: pub.workspace_id,
            parentId: pub.id,
            destinationId: dest.id,
          });
        }
      } catch (mediaErr) {
        // Non-fatal: the chip state already changed; just surface the media
        // error so the user can retry from the checklist drawer.
        console.warn("[publisher] pending-media sync failed", mediaErr);
      }
      await onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusyKey(null);
    }
  };

  // ---- Multi-destination fan-out (tracker #36b) -------------------------
  // Eligible targets = filtered live connectors not yet marked sent + filtered
  // manual destinations not yet ticked. The popover lets the user check a
  // subset and fire them all in one click.
  const eligibleConnectors = filteredConnectors.filter(
    (c) => isConnectorLive(c) && !connectorDoneIds.has(c.id),
  );
  const eligibleManual = filteredManualDests.filter((d) => !manualMarkedIds.has(d.id));
  const eligibleCount = eligibleConnectors.length + eligibleManual.length;

  const [fanOutOpen, setFanOutOpen] = useState(false);
  const [fanOutPicks, setFanOutPicks] = useState<Set<string>>(new Set());
  const [fanOutBusy, setFanOutBusy] = useState(false);

  const togglePick = (id: string) => {
    setFanOutPicks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runFanOut = async () => {
    if (fanOutPicks.size === 0) return;
    setFanOutBusy(true);
    let ok = 0;
    let fail = 0;
    let copied = false;
    for (const c of eligibleConnectors) {
      if (!fanOutPicks.has(`c:${c.id}`)) continue;
      try {
        const dispatch = await runPublicationDispatch(pub.workspace_id, {
          publicationId: pub.id,
          connectorId: c.id,
          allowStale: false,
        });
        await recordConnectorDispatch({
          workspaceId: pub.workspace_id,
          publicationId: pub.id,
          connectorId: c.id,
          dispatchId: dispatch.id,
          createdByUserId: userId,
        });
        ok++;
      } catch (err) {
        fail++;
        toast.error(
          `${c.display_name || c.connector_type}: ${err instanceof Error ? err.message : "push failed"}`,
        );
      }
    }
    for (const d of eligibleManual) {
      if (!fanOutPicks.has(`m:${d.id}`)) continue;
      try {
        await setManualChipMarked({
          workspaceId: pub.workspace_id,
          publicationId: pub.id,
          manualDestinationId: d.id,
          marked: true,
          createdByUserId: userId,
        });
        try {
          await seedManualPendingMedia({
            scope: "publication",
            workspaceId: pub.workspace_id,
            parentId: pub.id,
            destinationId: d.id,
            body: pub.prepared_body ?? "",
          });
        } catch (mediaErr) {
          console.warn("[publisher] pending-media sync failed", mediaErr);
        }
        if (!copied) {
          await copyPublicationText(pub.prepared_body ?? "");
          copied = true;
        }
        ok++;
      } catch (err) {
        fail++;
        toast.error(`${d.label}: ${err instanceof Error ? err.message : "mark failed"}`);
      }
    }
    setFanOutBusy(false);
    setFanOutOpen(false);
    setFanOutPicks(new Set());
    if (ok > 0) {
      toast.success(
        `Fan-out complete · ${ok} sent${fail ? ` · ${fail} failed` : ""}${copied ? " · body copied to clipboard" : ""}`,
      );
    }
    await onChanged();
  };



  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className="space-y-2 rounded-md border border-border bg-card p-3 text-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{publishTypeLabel}</Badge>
            <span className="truncate text-xs font-medium">
              {pub.prepared_title || briefTitle}
            </span>
          </div>
          <p className="line-clamp-2 text-[11px] text-muted-foreground">
            {pub.prepared_excerpt || pub.prepared_body?.slice(0, 200) || "—"}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {briefTitle} · {new Date(pub.updated_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => void copyPublicationText(pub.prepared_body ?? "")}
            title="Copy body"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() =>
              downloadPublicationFile(
                pub.prepared_body ?? "",
                pub.prepared_title || briefTitle,
                pub.destination_format as ContentDestinationFormat,
              )
            }
            title="Download"
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
          <CollapsibleTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={expanded ? "Collapse" : "Expand"}>
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>

      {/* Chip row — always visible */}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
        {connectors.length === 0 && manualDests.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            No destinations configured. Add connectors in Settings → Integrations or manual destinations above.
          </p>
        ) : filteredConnectors.length === 0 && filteredManualDests.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            No destinations match this content type. Open <span className="font-medium">Manage manual destinations</span> above to enable some, or add a matching connector in Settings → Integrations.
          </p>
        ) : null}
        {filteredConnectors.map((c) => (
          <ConnectorChip
            key={c.id}
            connector={c}
            done={connectorDoneIds.has(c.id)}
            busy={busyKey === `c:${c.id}`}
            pending={pendingByDest.get(c.id)?.pending ?? 0}
            onPush={() => void handlePushConnector(c)}
          />
        ))}
        {filteredManualDests.map((d) => (
          <ManualChip
            key={d.id}
            destination={d}
            marked={manualMarkedIds.has(d.id)}
            busy={busyKey === `m:${d.id}`}
            body={pub.prepared_body ?? ""}
            pending={pendingByDest.get(d.id)?.pending ?? 0}
            onToggle={(v) => void handleToggleManual(d, v)}
          />
        ))}
        {connectors.length === 0 && filteredManualDests.length > 0 ? (
          <Link
            to="/settings"
            className="ml-1 text-[11px] text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-primary"
          >
            + Add a publishing connector
          </Link>
        ) : null}
        {eligibleCount >= 2 ? (
          <Popover open={fanOutOpen} onOpenChange={setFanOutOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="ml-auto h-7 gap-1 px-2 text-[11px]"
                title="Publish to multiple destinations at once"
              >
                <Send className="h-3 w-3" /> Publish to…
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3">
              <p className="mb-2 text-xs font-medium">Send to multiple destinations</p>
              <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
                {eligibleConnectors.map((c) => {
                  const id = `c:${c.id}`;
                  return (
                    <label
                      key={id}
                      className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-accent"
                    >
                      <Checkbox
                        checked={fanOutPicks.has(id)}
                        onCheckedChange={() => togglePick(id)}
                      />
                      <span className="flex-1 truncate">
                        {c.display_name || c.connector_type}
                      </span>
                      <Badge variant="secondary" className="h-4 px-1 text-[9px]">
                        connector
                      </Badge>
                    </label>
                  );
                })}
                {eligibleManual.map((d) => {
                  const id = `m:${d.id}`;
                  return (
                    <label
                      key={id}
                      className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-accent"
                    >
                      <Checkbox
                        checked={fanOutPicks.has(id)}
                        onCheckedChange={() => togglePick(id)}
                      />
                      <span className="flex-1 truncate">{d.label}</span>
                      <Badge variant="outline" className="h-4 px-1 text-[9px]">
                        manual
                      </Badge>
                    </label>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <button
                  type="button"
                  className="text-[10px] text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => {
                    const all = new Set<string>();
                    eligibleConnectors.forEach((c) => all.add(`c:${c.id}`));
                    eligibleManual.forEach((d) => all.add(`m:${d.id}`));
                    setFanOutPicks(fanOutPicks.size === all.size ? new Set() : all);
                  }}
                >
                  {fanOutPicks.size === eligibleCount ? "Clear" : "Select all"}
                </button>
                <Button
                  size="sm"
                  className="h-7 gap-1 px-2 text-[11px]"
                  disabled={fanOutBusy || fanOutPicks.size === 0}
                  onClick={() => void runFanOut()}
                >
                  <Send className="h-3 w-3" />
                  {fanOutBusy ? "Sending…" : `Send to ${fanOutPicks.size}`}
                </Button>
              </div>
              {eligibleManual.some((d) => fanOutPicks.has(`m:${d.id}`)) ? (
                <p className="mt-2 text-[10px] text-muted-foreground">
                  Manual destinations will be ticked off and the body copied to your clipboard.
                </p>
              ) : null}
            </PopoverContent>
          </Popover>
        ) : null}
      </div>


      <CollapsibleContent>
        <pre className="mt-2 max-h-96 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-[11px] leading-relaxed whitespace-pre-wrap">
          {pub.prepared_body ?? ""}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
};

/* ---------------------------------- Page ---------------------------------- */

const Publisher = () => {
  const { active } = useWorkspace();
  const { user } = useAuth();
  const workspaceId = active?.id ?? null;
  const userId = user?.id ?? null;
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [manualDests, setManualDests] = useState<ManualDestination[]>([]);
  const [destRows, setDestRows] = useState<PublicationDestination[]>([]);
  const [pendingCounts, setPendingCounts] = useState<
    Map<string, Map<string, PendingMediaCount>>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [showManageDestinations, setShowManageDestinations] = useState(false);

  const refresh = useCallback(async () => {
    if (!workspaceId || !userId) {
      setSnapshot(null);
      setManualDests([]);
      setDestRows([]);
      setPendingCounts(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [snap, manual, dests] = await Promise.all([
        fetchComposerSnapshot(workspaceId),
        ensureSeededManualDestinations(workspaceId, userId),
        listPublicationDestinations(workspaceId),
      ]);
      setSnapshot(snap);
      setManualDests(manual);
      setDestRows(dests);
      // Counts are a separate, best-effort read so a failure here doesn't
      // hide the inbox.
      try {
        const pubIds = snap.briefPublications.filter((p) => p.status === "succeeded").map((p) => p.id);
        const counts = await listPendingMediaCounts("publication", workspaceId, pubIds);
        setPendingCounts(counts);
      } catch (countErr) {
        console.warn("[publisher] pending counts failed", countErr);
        setPendingCounts(new Map());
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load Publisher");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Stage R — live-refresh chip badges when pending-media rows change in
  // another tab or by another teammate. We only refetch counts (cheap) and
  // never the full inbox.
  const pubIdsKey = useMemo(
    () =>
      (snapshot?.briefPublications ?? [])
        .filter((p) => p.status === "succeeded")
        .map((p) => p.id)
        .sort()
        .join(","),
    [snapshot],
  );
  useEffect(() => {
    if (!workspaceId || !pubIdsKey) return;
    const pubIds = pubIdsKey.split(",");
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          const counts = await listPendingMediaCounts("publication", workspaceId, pubIds);
          setPendingCounts(counts);
        } catch (err) {
          console.warn("[publisher] realtime counts refetch failed", err);
        }
      }, 250);
    };
    const channel = supabase
      .channel(`publisher-pending-media-${workspaceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "content_publication_pending_media",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        schedule,
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [workspaceId, pubIdsKey]);


  const { inboxPubs, briefById, connectors } = useMemo(() => {
    if (!snapshot) {
      return {
        inboxPubs: [] as ContentBriefPublication[],
        briefById: new Map<string, ContentBrief>(),
        connectors: [] as ContentPublishingConnector[],
      };
    }
    // Show every succeeded publication. Sent/Archive split is gone — the
    // chip checkmarks are the completion signal now. Filter out only
    // explicitly archived/promoted (which exist as Composer > Vault > Archive).
    const pubs = snapshot.briefPublications
      .filter((p) => p.status === "succeeded")
      .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
    return {
      inboxPubs: pubs,
      briefById: new Map(snapshot.briefs.map((b) => [b.id, b])),
      connectors: snapshot.publishingConnectors,
    };
  }, [snapshot]);

  if (!workspaceId || !userId) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <AppSeo title="Publisher" />
        <p className="text-sm text-muted-foreground">
          Select a workspace from the top tabs to use the Publisher.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4 lg:p-6">
      <AppSeo title="Publisher" />
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-semibold">Publisher</h1>
          <p className="text-xs text-muted-foreground">
            Push prepared pieces to your connectors and manual destinations. Published pieces stay
            in <Link to="/composer" className="underline">Composer › Vault › Archive</Link>.
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/composer">
            <ArrowLeft className="h-4 w-4" /> Composer
          </Link>
        </Button>
      </header>

      <Collapsible open={showManageDestinations} onOpenChange={setShowManageDestinations}>
        <Card className="shadow-soft">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer pb-2 pt-3">
              <CardTitle className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2">
                  <SettingsIcon className="h-4 w-4 text-primary" />
                  Manage manual destinations
                </span>
                {showManageDestinations ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </CardTitle>
              <CardDescription className="text-xs">
                Add, rename, or remove copy-and-paste destination chips for this workspace.
              </CardDescription>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <ManualDestinationsEditor
                workspaceId={workspaceId}
                userId={userId}
                onChanged={() => void refresh()}
              />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <Card className="flex min-h-0 flex-1 flex-col shadow-soft">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2">
              <InboxIcon className="h-4 w-4 text-primary" />
              Inbox
            </span>
            <Badge variant="outline" className="text-[10px]">{inboxPubs.length}</Badge>
          </CardTitle>
          <CardDescription className="text-xs">
            Every prepared publication. Push to connectors or tick a manual chip when you've
            copy-pasted it. Nothing is deleted from here.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 space-y-2 overflow-y-auto">
          {loading && !snapshot ? (
            <p className="rounded-md border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              Loading…
            </p>
          ) : inboxPubs.length === 0 ? (
            <p className="rounded-md border border-dashed border-border p-12 text-center text-xs text-muted-foreground">
              Nothing here yet. Push a draft from <Link to="/composer" className="underline">Composer</Link> to get started.
            </p>
          ) : (
            inboxPubs.map((pub) => (
              <PublicationCard
                key={pub.id}
                pub={pub}
                brief={briefById.get(pub.piece_id)}
                connectors={connectors}
                pendingByDest={pendingCounts.get(pub.id) ?? new Map()}
                manualDests={manualDests}
                destRows={destRows.filter((r) => r.publication_id === pub.id)}
                userId={userId}
                onChanged={refresh}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Publisher;
